import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'

export default function InviteLinks() {
  const [subgroups, setSubgroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [generatedLinks, setGeneratedLinks] = useState([])
  const [selectedSubgroup, setSelectedSubgroup] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')

  useEffect(() => {
    loadSubgroups()
    loadExistingLinks()
  }, [])

  const loadSubgroups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data, error } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 3)
        .order('name')

      if (error) throw error
      setSubgroups(data || [])
    } catch (error) {
      console.error('Load subgroups error:', error)
    }
  }

  const loadExistingLinks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data, error } = await supabase
        .from('invite_links')
        .select('*, groups(name)')
        .eq('org_id', userData.org_id)
        .eq('used', false)
        .order('created_at', { ascending: false })

      if (error) throw error
      setGeneratedLinks(data || [])
    } catch (error) {
      console.error('Load links error:', error)
    }
  }

  const generateLink = async (groupId, isGeneral = false) => {
    setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 30)

      const { data, error } = await supabase
        .from('invite_links')
        .insert({
          org_id: userData.org_id,
          group_id: isGeneral ? null : groupId,
          token: token,
          recipient_email: recipientEmail || null,
          created_by: user.id,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single()

      if (error) throw error

      await loadExistingLinks()
      setRecipientEmail('')
      alert('Invite link generated successfully!')
    } catch (error) {
      console.error('Generate link error:', error)
      alert('Failed to generate link')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (token, groupId) => {
    const baseUrl = window.location.origin
    const link = groupId 
      ? baseUrl + '/signup?token=' + token + '&group=' + groupId
      : baseUrl + '/signup?token=' + token
    
    navigator.clipboard.writeText(link)
    alert('Link copied to clipboard!')
  }

  return (
    <DashboardLayout>
      <PageHeader title="Generate Invite Links" description="Create invitation links for new members">
        <Link
          to="/admin/members"
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </Link>
      </PageHeader>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
          
          {/* Generate for Subgroup (Member) */}
          <Card title="Member Invite (Subgroup)" description="Generate link for members to join a specific subgroup. They will get a QR code.">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Subgroup</label>
                <select
                  value={selectedSubgroup}
                  onChange={(e) => setSelectedSubgroup(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Choose a subgroup...</option>
                  {subgroups.map(subgroup => (
                    <option key={subgroup.id} value={subgroup.id}>
                      {subgroup.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Recipient Email (Optional)
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="w-full px-3 py-2 border rounded-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  For tracking only. Link works regardless.
                </p>
              </div>

              <button
                onClick={() => generateLink(selectedSubgroup)}
                disabled={!selectedSubgroup || loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Member Link'}
              </button>
            </div>
          </Card>

          {/* Generate General Link (Unassigned) */}
          <Card title="General Invite (Unassigned)" description="Generate link for unassigned members. They will not get a QR code until assigned.">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Recipient Email (Optional)
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="person@example.com"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <button
                onClick={() => generateLink(null, true)}
                disabled={loading}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate General Link'}
              </button>
            </div>
          </Card>
        </div>

        {/* Generated Links List */}
        <div className="max-w-5xl mt-6">
          <Card title="Generated Links" description="Active invite links (expires in 30 days)">
            <div className="divide-y">
              {generatedLinks.length === 0 ? (
                <div className="p-6 text-center text-gray-600">
                  No links generated yet
                </div>
              ) : (
                generatedLinks.map(link => {
                  const fullLink = link.group_id
                    ? window.location.origin + '/signup?token=' + link.token + '&group=' + link.group_id
                    : window.location.origin + '/signup?token=' + link.token
                  
                  return (
                    <div key={link.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={'px-2 py-1 text-xs rounded-full font-medium ' + (link.group_id ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800')}>
                            {link.group_id ? 'Member' : 'Unassigned'}
                          </span>
                          {link.groups && (
                            <span className="ml-2 font-medium">{link.groups.name}</span>
                          )}
                        </div>
                        <button
                          onClick={() => copyToClipboard(link.token, link.group_id)}
                          className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
                        >
                          Copy Link
                        </button>
                      </div>
                      
                      <div className="text-sm text-gray-600 break-all bg-gray-50 p-2 rounded font-mono">
                        {fullLink}
                      </div>
                      
                      {link.recipient_email && (
                        <p className="text-xs text-gray-500 mt-2">
                          For: {link.recipient_email}
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-1">
                        Expires: {new Date(link.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  )
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}